/**
 * NativeSamplePrewarm
 *
 * Native-only. Pushes the top few For You feed sample URLs to the native
 * audio plugin's preroll cache (engine.preload) so the session's FIRST
 * tap-to-play doesn't pay cold DNS + TLS + buffering to the sample CDN —
 * the reason the first play was seconds slower than every play after it.
 *
 * Strict rules (mirrors NativeLockScreenRefill):
 *   - Does NOT mutate Redux (read-only consumer of state.feed.items).
 *   - Renders nothing — pure side-effect component.
 *   - No-op on web/Electron (isNative gate + engine unavailable).
 *
 * Side-effect guarantees:
 *   - Waits SETTLE_MS after the feed lands before preloading, so the
 *     launch burst (auth, feed JSON, artwork) wins the radio first.
 *   - Bounded: PREWARM_COUNT urls, each buffered natively only up to
 *     ~10s of a 30s sample (~350KB) — see the plugin's preroll cache.
 *   - The native side never touches the audio session or the lock-screen
 *     card for prerolled items.
 */

import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getNativePlaybackEngine } from '../playback/NativePlaybackEngine';
import { isNative } from '../utils/webOnly';

// Mirrors the plugin's preroll cache size. 5 because users tap below the
// fold too — the 08-10 tap capture caught a first tap on feed item #7.
const PREWARM_COUNT = 5;
// Small yield so the feed render + first artwork requests win the radio
// first. Measured on-device: feed JSON lands ~2.7s after cold launch, so
// with 1s settle the preroll arms ~3.7s in — ahead of a typical quick
// first tap. The preroll is bounded (~1MB) so contention is negligible.
const SETTLE_MS = 1000;
// Plugin registration happens after React mounts (capacitorDidLoad) —
// retry a few times if the engine isn't available when the feed lands.
const ENGINE_RETRY_MS = 1000;
const ENGINE_RETRIES = 5;

function NativeSamplePrewarm() {
    const feedItems = useSelector((s) => s.feed.items);
    const lastSigRef = useRef('');

    useEffect(() => {
        if (!isNative) return undefined;

        const urls = (feedItems || [])
            .map((it) => {
                const t = it?.track || it;
                return t?.previewUrl || t?.sampleUrl || t?.preview_url || t?.preview || '';
            })
            .filter(Boolean)
            .slice(0, PREWARM_COUNT);
        if (!urls.length) return undefined;

        const sig = urls.join('|');
        if (sig === lastSigRef.current) return undefined;

        let cancelled = false;
        let retriesLeft = ENGINE_RETRIES;
        let timer = null;

        const firePreload = () => {
            if (cancelled) return;
            const engine = getNativePlaybackEngine();
            if (!engine.isAvailable()) {
                if (retriesLeft-- > 0) timer = setTimeout(firePreload, ENGINE_RETRY_MS);
                return;
            }
            lastSigRef.current = sig;
            engine.preload(urls);
        };

        timer = setTimeout(firePreload, SETTLE_MS);
        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [feedItems]);

    return null;
}

export default NativeSamplePrewarm;
